<template>
  <div class="reservation">
    <div class="card-header bg-default">
      <router-link :to="{ path: `/fleet/reservations/${reservation.uuid}` }">
        Reservation #{{ reservation.reservationId }}
      </router-link>
      <label class="badge badge-danger" v-if="reservation.maintenance">Maintenance Reservation</label>
      <label class="badge badge-warning" v-if="!reservation.isPersonal && !reservation.FinanceCode">Business Reservation</label>
      <label class="badge badge-success" v-if="reservation.isPersonal && !reservation.FinanceCode">Personal Reservation</label>
      <label class="badge" :class="startedStatus.class" v-if="startedStatus">
        {{ startedStatus.text }}
      </label>
      <label class="badge" :class="completedStatus.class" v-if="completedStatus">
        {{ completedStatus.text }}
      </label>
      <label class="badge badge-info" v-if="reservation.refunded">
        Refunded
      </label>
      <label class="badge badge-primary" v-if="reservation.LotUuid != reservation.DestinationLotUuid">
        One Way Trip
      </label>
      <label class="badge badge-success" v-if="reservation.acknowledged">Acknowledged</label>
      <label class="badge badge-default" v-if="!reservation.acknowledged">Not Sent</label>
    </div>
    <div class="list-group list-group-flush">
      <div class="list-group-item" :class="billingStatus.class" v-if="billingStatus">
        <!-- Billing Status -->
        <strong>Billing Status: </strong>
        <span class="fa-stack">
          <span class="fa fa-circle fa-stack-2x"></span>
          <span class="fa fa-stack-1x fa-inverse" :class="billingStatus.icon"></span>
        </span>
        {{ billingStatus.text }}
      </div>
      <div class="list-group-item">
        <!-- Reservation Details -->
        <strong><span class="fa fa-fw fa-user"></span> Driver: </strong>
        <router-link :to="{ path: `/membership/members/${reservation.Account.uuid}`}">
          {{ reservation.Account.dropdownLabel }} -
          <strong>{{ reservation.Account.AccountType.name }}</strong>
        </router-link>
        <span v-if="reservation.Account.Smartcard" class="badge badge-success ml-2">
          <span class="fa fa-fw fa-id-card"></span><strong> Smartcard:</strong> {{ reservation.Account.Smartcard.cardNumber }}
        </span>
      </div>
      <div class="list-group-item" v-if="reservation.AdminResponsible">
        <strong><span class="fa fa-fw fa-user-circle"></span> Admin Responsible: </strong>
        <router-link :to="{ path: `/membership/members/${reservation.AdminResponsible.uuid}`}">
          {{ reservation.AdminResponsible.dropdownLabel }}
        </router-link>
      </div>
      <div class="list-group-item" v-if="reservation.Account.businessName">
        <strong>Business:</strong> {{ reservation.Account.businessName }}
      </div>
      <div class="list-group-item">
        <span class="fa fa-fw fa-hourglass-start"></span><strong>Start:</strong>
          {{ reservation.startDate | date_format(reservation.Lot.timezone) }}<br/>
        <span class="fa fa-fw fa-hourglass-end"></span><strong>End:</strong>
          {{ reservation.endDate | date_format(reservation.Lot.timezone) }}
      </div>
      <div class="list-group-item" v-if="reservation.completed && !reservation.cancelled">
        <p>
          <strong>Member Entered:</strong>
          {{ reservation.completedStartDate | date_format(reservation.Lot.timezone) }}<br/>
          <strong>Reservation Completed:</strong>
          {{ reservation.completedEndDate | date_format(reservation.Lot.timezone) }}
        </p>
        <p><strong>Late Fee:</strong> ${{ reservation.lateFee }}</p>
        <p><strong>KM Fee:</strong> ${{ reservation.kmFee }}</p>
      </div>
      <div class="list-group-item">
        <strong>Total Charges:</strong> ${{ reservation.quotedCost }}
        <div v-if="reservation.ReservationPromo">
          <label class="badge badge-info">
            <span class="fa fa-fw fa-dollar-sign"></span> <strong>Promotional Code - {{ reservation.ReservationPromo.code }}</strong>
          </label>
        </div>
        <div v-if="reservation.percentDiscount > 0">
          <label class="badge badge-primary">
            <span class="fa fa-fw fa-percent"></span> <strong>Discount - {{ reservation.percentDiscount }}%</strong>
          </label>
        </div>
      </div>
      <div class="list-group-item">
        <strong><span class="fa fa-university fa-fw"></span> Finance Code:</strong>
        <label class="badge badge-default" v-if="reservation.FinanceCode">{{ reservation.FinanceCode.code }} - {{ reservation.FinanceCode.description }}</label>
      </div>
      <div class="list-group-item">
        <strong><span class="fa fa-file-alt fa-fw"></span> Memo: </strong>{{ reservation.memo }}
      </div>
      <div class="list-group-item">
        <div class="row">
          <div class="col-md-6">
            <img style="max-height: 100px; max-width: 150px;" class="m-2 float-left" :src="carImage"/>
            <p>
              <router-link :to="{ path: `/fleet/vehicles/${reservation.Car.uuid}` }">
              {{ reservation.Car.name }} (#{{ reservation.Car.convadisId }}
              - {{ reservation.Car.rego }}) - {{ reservation.Car.CarType.name }}
              </router-link>
            </p>
            <p>
              <strong><span class="fa fa-map-marker-alt"></span> Address:</strong>
              {{ reservation.Lot.address }}<br/>
              <em>{{ reservation.Lot.addressMemo }}</em>
            </p>
            <template v-if="reservation.LotUuid !== reservation.DestinationLotUuid">
              <p>
                <strong><span class="fa fa-map-marker-alt"></span> Return</strong>
                {{ reservation.DestinationLot.address }}<br/>
                <em>{{ reservation.DestinationLot.addressMemo }}</em>
              </p>
            </template>
          </div>
          <div class="col-md-6">
            <template v-if="reservation.AccountNotes.length > 0">
              <div class="card">
                <div class="card-body">
                  <account-note-display :data="reservation.AccountNotes[0]"></account-note-display>
                </div>
              </div>
            </template>
            <div class="card" v-else>
              <div class="card-body">
                No Notes have been made for this reservation
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer text-right">
      <a v-if="reservation.isPersonal && isFinanceAdmin" class="btn btn-default"
      :href="stripeURL + reservation.reservationId" target="_blank">
        <span class="fa fab-cc-stripe"></span> View Charges
      </a>
      <a target="_blank" class="btn btn-default" :href="googleLink" >
        <span class="fa fa-map"></span> Map
      </a>
      <router-link class="btn btn-primary" :to="{ path: `/fleet/reservations/${reservation.uuid}` }">
        <span class="fa fa-search"></span> View
      </router-link>
      <dropdown-input label="Car Commands"
        label-icon="fa-wifi"
        :actions="carCommands"
        :dropup="true"
        @action="executeAction($event, reservation.Car)">
      </dropdown-input>
      <dropdown-input label="Actions"
        label-icon="fa-calendar-alt"
        :actions="reservationActions(reservation, loggedInUser)"
        :dropup="true"
        @action="executeAction($event, reservation)">
      </dropdown-input>
    </div>
  </div>
</template>

<script>
import AccountNoteDisplay from '@/components/displays/AccountNoteDisplay'
import DropdownInput from '@/components/inputs/DropdownInput'
import moment from 'moment-timezone'
import { mapGetters, mapActions } from 'vuex'
import {
  ADD_RESERVATION_NOTE,
  CREATE_RESERVATION_CHARGE,
  EDIT_RESERVATION,
  CANCEL_RESERVATION,
  INSTANT_RESERVATION,
  UNLOCK_CAR,
  LOCK_CAR,
  IGNITION_ON,
  IGNITION_OFF,
  IMMOBILIZE_VEHICLE,
  GPS_LOCATION,
  RESEND_RESERVATION,
  REFUND_RESERVATION,
  GENERATE_RESERVATION_INVOICE,
  SEND_CANCEL_MESSAGE
} from '@/vuex/types'

export default {
  components: {
    AccountNoteDisplay,
    DropdownInput
  },
  name: 'Reservation',
  props: {
    object: {
      type: Object
    },
    isFinanceAdmin: {
      type: Boolean,
      default () {
        return false
      }
    }
  },
  computed: {
    googleLink () {
      return 'https://maps.google.com/?q=' +
      this.reservation.Lot.latitude + ',' +
      this.reservation.Lot.longitude
    },
    carImage () {
      return 'https://techcar-dev.s3.amazonaws.com/' +
      this.reservation.Car.CarType.AmazonImage.objectKey
    },
    startedStatus () {
      if (this.reservation.started) {
        return { class: 'badge-info', text: 'Started' }
      } else if (moment(this.reservation.startDate).isAfter(moment())) {
        return { class: 'badge-default', text: 'Upcoming' }
      }
    },
    completedStatus () {
      if (this.reservation.completed) {
        if (this.reservation.cancelled) {
          return { class: 'badge-warning', text: 'Completed Early' }
        }
        return { class: 'badge-success', text: 'Completed' }
      } else if (this.reservation.cancelled) {
        return { class: 'badge-danger', text: 'Cancelled' }
      }
    },
    billingStatus () {
      let textClass, icon, text
      let startedStatus = this.startedStatus
      if (!this.reservation.refunded && (this.reservation.completed || this.reservation.cancelled)) {
        textClass = 'text-success'
        icon = 'fa-dollar-sign'
        text = 'Billed'
      } else if (!this.reservation.completed && !this.reservation.cancelled) {
        if (startedStatus && startedStatus.text.includes('Upcoming')) {
          textClass = 'text-warning'
          icon = 'fa-clock'
          text = 'Billed waiting to be started'
        } else if (startedStatus && startedStatus.text.includes('Started')) {
          textClass = 'text-warning'
          icon = 'fa-clock'
          text = 'Waiting Trip Data'
        } else {
          textClass = 'text-muted'
          icon = 'fa-clock'
          text = 'Billed and not used'
        }
      } else if (this.reservation.cancelled && !this.reservation.refunded) {
        textClass = 'text-danger'
        icon = 'fa-ban'
        text = 'Billed and cancelled'
      } else if (this.reservation.refunded) {
        textClass = 'text-danger'
        icon = 'fa-exchange-alt'
        text = 'Refunded'
      }
      return { class: textClass, icon, text }
    },
    reservation () {
      return {
        started: false,
        completed: false,
        cancelled: false,
        refunded: false,
        reservationId: 0,
        Account: {
          AccountType: {},
          Smartcard: {}
        },
        Car: {
          CarType: {
            AmazonImage: {
              objectKey: 'spacer.gif'
            }
          }
        },
        Lot: {},
        DestinationLot: {},
        AccountNotes: [],
        ...this.object,
        ...this.updatedReservation
      }
    },
    ...mapGetters([
      'loadedReservation',
      'carCommands',
      'reservationActions',
      'carMessages',
      'carMessagesTotal',
      'loggedInUser'
    ])
  },
  data () {
    return {
      stripeURL: 'https://dashboard.stripe.com/search?query=%23',
      updatedReservation: {}
    }
  },
  methods: {
    async executeAction (action, payload) {
      let result = await this[action](payload)
      if (result && result.updated) this.updatedReservation = result.updated
    },
    ...mapActions([
      CREATE_RESERVATION_CHARGE,
      ADD_RESERVATION_NOTE,
      RESEND_RESERVATION,
      INSTANT_RESERVATION,
      UNLOCK_CAR,
      LOCK_CAR,
      IGNITION_ON,
      IGNITION_OFF,
      IMMOBILIZE_VEHICLE,
      GPS_LOCATION,
      EDIT_RESERVATION,
      CANCEL_RESERVATION,
      REFUND_RESERVATION,
      GENERATE_RESERVATION_INVOICE,
      SEND_CANCEL_MESSAGE
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/cards/Reservation.vue